import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Facebook, Twitter, Copy, Share2 } from "lucide-react";
import { api } from "../lib/api";

// Statuses that represent adjudicated convictions (or equivalents we
// treat as such for public-share copy).
const CONVICTED_STATUSES = new Set([
  "Convicted",
  "Pleaded Guilty",
  "Registered",
]);

// Statuses that represent active criminal proceedings prior to verdict.
const ON_TRIAL_STATUSES = new Set([
  "On Trial — Pending",
  "Indicted",
  "Charged",
]);

function buildCaption({ convicted, onTrial }) {
  const url = "https://www.pawsonrecord.com/";
  return (
    `Paws on Record tracks ${convicted} convicted and ${onTrial} on-trial animal abusers across the United States — searchable by city, with a 50-mile radius check for shelters, rescues, and anyone screening adoption or foster placements.\n\n` +
    `Every share could prevent another animal from being adopted by their abuser.\n\n` +
    `${url}\n` +
    `#PawsOnRecord #AnimalCruelty #ProtectAnimals #AdoptDontAbuse`
  );
}

export default function ShareRegistryButton() {
  const [counts, setCounts] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api
      .get("/abusers/stats")
      .then((r) => {
        const list = r.data?.by_status || [];
        let convicted = 0;
        let onTrial = 0;
        for (const row of list) {
          if (CONVICTED_STATUSES.has(row.status)) convicted += row.count;
          else if (ON_TRIAL_STATUSES.has(row.status)) onTrial += row.count;
        }
        setCounts({ convicted, onTrial });
      })
      .catch(() => {});
  }, []);

  if (!counts || (counts.convicted === 0 && counts.onTrial === 0)) return null;

  const caption = buildCaption(counts);
  const shareUrl = "https://www.pawsonrecord.com/";

  const onFacebook = () => {
    const u = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(caption)}`;
    window.open(u, "_blank", "noopener,noreferrer,width=640,height=640");
  };
  const onTwitter = () => {
    const text = caption.length > 260 ? caption.slice(0, 257) + "…" : caption;
    const u = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(u, "_blank", "noopener,noreferrer,width=640,height=640");
  };
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Registry caption copied. Paste anywhere.");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually.");
    }
  };

  return (
    <div
      className="mt-8 border border-zinc-900 bg-zinc-900 text-white p-5"
      data-testid="share-registry-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <Share2 size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="label-eyebrow text-red-500">Help spread the word</div>
            <p className="text-sm mt-1 leading-relaxed">
              <span className="font-display font-black text-base">
                {counts.convicted.toLocaleString()} convicted
              </span>{" "}
              and{" "}
              <span className="font-display font-black text-base">
                {counts.onTrial.toLocaleString()} on-trial
              </span>{" "}
              animal abusers tracked. Share to protect adopters and foster
              animals nationwide.
            </p>
          </div>
        </div>

        {/* Mobile: collapsible to save space. Desktop: always visible. */}
        <div className="hidden sm:flex flex-wrap items-center gap-2 flex-shrink-0">
          <ShareBtn onClick={onFacebook} testid="share-registry-fb">
            <Facebook size={13} /> Facebook
          </ShareBtn>
          <ShareBtn onClick={onTwitter} testid="share-registry-x">
            <Twitter size={13} /> X
          </ShareBtn>
          <ShareBtn onClick={onCopy} testid="share-registry-copy">
            <Copy size={13} /> Copy
          </ShareBtn>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="sm:hidden inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border border-white text-white"
          data-testid="share-registry-toggle"
        >
          <Share2 size={13} /> {open ? "Hide" : "Share"}
        </button>
      </div>

      {open && (
        <div className="sm:hidden flex flex-wrap items-center gap-2 mt-4">
          <ShareBtn onClick={onFacebook} testid="share-registry-fb-m">
            <Facebook size={13} /> Facebook
          </ShareBtn>
          <ShareBtn onClick={onTwitter} testid="share-registry-x-m">
            <Twitter size={13} /> X
          </ShareBtn>
          <ShareBtn onClick={onCopy} testid="share-registry-copy-m">
            <Copy size={13} /> Copy
          </ShareBtn>
        </div>
      )}
    </div>
  );
}

function ShareBtn({ children, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border border-white text-white hover:bg-white hover:text-zinc-900 transition-colors"
      data-testid={testid}
    >
      {children}
    </button>
  );
}
